import { useEffect, useRef, useState } from "react";

/**
 * Autoplaying decorative video that only starts downloading once it is near the
 * viewport. The poster carries the visual until then, so the video bytes never
 * compete with the initial page load.
 */
export function LazyVideo({
  src,
  poster,
  className = "",
}: {
  src: string;
  poster: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || load) return;

    // No IntersectionObserver (very old browsers): just load it.
    if (typeof IntersectionObserver === "undefined") {
      setLoad(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLoad(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [load]);

  // Once the source is attached, kick off playback. autoPlay alone doesn't
  // always fire when the source is added after mount.
  useEffect(() => {
    if (load) ref.current?.play().catch(() => {});
  }, [load]);

  return (
    <video
      ref={ref}
      src={load ? src : undefined}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      className={className}
    />
  );
}
